import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import { fetchBridgeTokenById } from '@/api/modules'
import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import TokenInfoSelect from '@/common/TokenInfoSelect'
import TokenSelect from '@/common/TokenSelect'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiCheckbox } from '@/ui/UiCheckbox'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  AddTokenInfoFormValues,
  buildAddTokenInfoMessage,
} from './tokenInfoMessageBuilder'

const addTokenInfoSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  selectedTokenId: z.string().optional(),
  selectedChainId: z.string().optional(),
  info: z.object({
    address: z.string().min(1, 'Token address is required'),
    decimals: z.string().min(1, 'Decimals value is required'),
    chainId: z.string().min(1, 'Chain ID is required'),
    tokenId: z
      .string()
      .min(1, 'Token ID is required')
      .refine(
        async val => {
          try {
            await fetchBridgeTokenById(val)
            return true
          } catch {
            return false
          }
        },
        { message: 'Token with this ID does not exist' },
      ),
    isWrapped: z.boolean(),
    minWithdrawalAmount: z.string().min(1, 'Min withdrawal amount is required'),
    commissionRate: z.string().min(1, 'Commission rate is required'),
  }),
})

export default function AddTokenInfoForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<AddTokenInfoFormValues>({
    resolver: zodResolver(addTokenInfoSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      selectedTokenId: '',
      selectedChainId: '',
      info: {
        address: '',
        decimals: '',
        chainId: '',
        tokenId: '',
        isWrapped: false,
        minWithdrawalAmount: '',
        commissionRate: '',
      },
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildAddTokenInfoMessage(watchedValues as AddTokenInfoFormValues)
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Add Token Info Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to add token information for a specific chain
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            {...form.register('creator')}
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>Token Info Details</h4>

          <div className='mt-5 flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel>Select Token</UiLabel>
              <TokenSelect<AddTokenInfoFormValues>
                control={form.control}
                name='selectedTokenId'
                onValueChange={(tokenId: string) =>
                  form.setValue('info.tokenId', tokenId, {
                    shouldValidate: true,
                  })
                }
                disabled={isSubmitting}
                placeholder='Select a token'
              />
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='tokenId'>Token ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='tokenId'
                  {...form.register('info.tokenId')}
                  placeholder='1'
                  disabled={isSubmitting}
                  readOnly
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.info?.tokenId && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.info.tokenId.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel>Select Chain</UiLabel>
              <TokenInfoSelect<AddTokenInfoFormValues>
                control={form.control}
                name='selectedChainId'
                tokenId={form.watch('info.tokenId')}
                mode='add'
                onValueChange={(chainId: string) =>
                  form.setValue('info.chainId', chainId, {
                    shouldValidate: true,
                  })
                }
                disabled={isSubmitting}
                placeholder='Select a chain'
              />
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='chainId'>Chain ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='chainId'
                  {...form.register('info.chainId')}
                  placeholder='ethereum'
                  disabled={isSubmitting}
                  readOnly
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.info?.chainId && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.info.chainId.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='address'>Token Address</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='address'
                  {...form.register('info.address')}
                  placeholder='0x...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.info?.address && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.info.address.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='decimals'>Decimals</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='decimals'
                  {...form.register('info.decimals')}
                  placeholder='18'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.info?.decimals && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.info.decimals.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='minWithdrawalAmount'>
                Min Withdrawal Amount
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='minWithdrawalAmount'
                  {...form.register('info.minWithdrawalAmount')}
                  placeholder='1000000'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.info?.minWithdrawalAmount && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.info.minWithdrawalAmount.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='commissionRate'>Commission Rate</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='commissionRate'
                  {...form.register('info.commissionRate')}
                  placeholder='0.001'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.info?.commissionRate && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.info.commissionRate.message}
                </p>
              )}
            </div>

            <div className='mb-2 flex items-center space-x-2'>
              <UiCheckbox
                id='isWrapped'
                checked={form.watch('info.isWrapped')}
                onCheckedChange={checked =>
                  form.setValue('info.isWrapped', !!checked)
                }
              />
              <UiLabel htmlFor='isWrapped' className='cursor-pointer'>
                Is Wrapped Token
              </UiLabel>
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'add-token-info-message.json')
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildAddTokenInfoMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
